export const MOVE_OVERVIEW_PATH = '/move';
export const MOVE_PLAN_PATH = '/create/templates/default/plan-move';
export const SCAFFOLDER_TASKS_PATH = '/create/tasks';

export type MoveCapability = {
  id: number;
  name: string;
  available: boolean;
};

export type MoveStage = {
  id: number;
  name: string;
  status: 'PLAN_INTAKE_AVAILABLE' | 'NOT_IMPLEMENTED';
  description: string;
  tools: string;
  capabilities: readonly MoveCapability[];
};

/**
 * Presentation-only grouping of the MOVE capability matrix. These values are a
 * product roadmap, not runtime migration state.
 */
export const MOVE_STAGES: readonly MoveStage[] = [
  {
    id: 1,
    name: 'Plan intake',
    status: 'PLAN_INTAKE_AVAILABLE',
    description:
      'Reuse a current approved assessment and give its frozen migration plan an operational name.',
    tools: 'Backstage, Horizon API, and PostgreSQL.',
    capabilities: [
      { id: 1, name: 'Approved plan intake', available: true },
      { id: 2, name: 'Jira / Temporal correlation', available: false },
    ],
  },
  {
    id: 2,
    name: 'Authorize and preflight',
    status: 'NOT_IMPLEMENTED',
    description:
      'Check the adapter, policy, identity, target, budget, source, and rollback prerequisites before any provider write.',
    tools:
      'Future OPA policy, AWS STS/IAM checks, and versioned adapter preflight.',
    capabilities: [
      { id: 3, name: 'OPA policy', available: false },
      { id: 4, name: 'Migration adapter', available: false },
      { id: 5, name: 'IAM and preflight', available: false },
    ],
  },
  {
    id: 3,
    name: 'Onboard and replicate',
    status: 'NOT_IMPLEMENTED',
    description:
      'Register the approved source agent, start replication, monitor it, and reconcile retries.',
    tools: 'Future Temporal orchestration and AWS MGN adapter.',
    capabilities: [
      { id: 6, name: 'Source-agent onboarding', available: false },
      { id: 7, name: 'Replication', available: false },
      { id: 8, name: 'Retry handling', available: false },
    ],
  },
  {
    id: 4,
    name: 'Test migration',
    status: 'NOT_IMPLEMENTED',
    description:
      'Launch an isolated test target and verify boot, services, and connectivity without cutting over.',
    tools: 'Future AWS MGN, smoke-test runner, and retained evidence store.',
    capabilities: [
      { id: 9, name: 'Test launch', available: false },
      { id: 10, name: 'Smoke checks', available: false },
    ],
  },
  {
    id: 5,
    name: 'Approve cutover',
    status: 'NOT_IMPLEMENTED',
    description:
      'Review test evidence, replication health, rollback readiness, and the change window before a separate approval.',
    tools: 'Future approval workflow, OPA policy, and Jira correlation.',
    capabilities: [{ id: 11, name: 'Cutover approval', available: false }],
  },
  {
    id: 6,
    name: 'Cutover and validate',
    status: 'NOT_IMPLEMENTED',
    description:
      'Quiesce source writes, launch the cutover target, validate it, and use the approved revert path if needed.',
    tools:
      'Future Temporal orchestration, AWS MGN adapter, and validation controls.',
    capabilities: [
      { id: 12, name: 'Launch', available: false },
      { id: 13, name: 'Validate or revert', available: false },
    ],
  },
  {
    id: 7,
    name: 'Evidence handoff',
    status: 'NOT_IMPLEMENTED',
    description:
      'Capture provider identifiers and outcomes, then hand verifiable evidence to ADAPT and PROVE.',
    tools: 'Future Sigstore, retained evidence storage, and Jira correlation.',
    capabilities: [{ id: 14, name: 'Evidence handoff', available: false }],
  },
  {
    id: 8,
    name: 'Finalize and retain source',
    status: 'NOT_IMPLEMENTED',
    description:
      'Finalize only after ADAPT/PROVE acceptance and keep source retirement as a separate decision.',
    tools:
      'Future OPA gates, Temporal orchestration, AWS MGN, and separate retirement approval.',
    capabilities: [
      { id: 15, name: 'Finalize after ADAPT / PROVE', available: false },
      {
        id: 16,
        name: 'Source retirement (separate decision)',
        available: false,
      },
    ],
  },
];

export const MOVE_NAVIGATION_ITEMS = [
  { label: 'Overview', path: MOVE_OVERVIEW_PATH },
  { label: 'Plan a move', path: MOVE_PLAN_PATH },
  {
    label: 'My task history (all stages)',
    path: SCAFFOLDER_TASKS_PATH,
  },
] as const;
